import { CheckCheckIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { actionFor } from '../heldActions';
import { useHeldActions } from '../heldActionsContext';
import { batchOf, type QueueGroup } from '../yourTurn';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * "Sign off all 3" on a parent's group (spec 154 US4): one action for
 * siblings waiting at the same gate, held and undone as one. Each row shows
 * the shared countdown and Undo while it is held.
 */
function BatchActionControl({
  group,
  onHeld,
}: {
  group: QueueGroup;
  onHeld: () => void;
}) {
  const { state, hold } = useHeldActions();
  const batch = batchOf(group);
  if (!batch) return null;
  // Not while any of them is already held on its own.
  if (batch.rows.some((row) => actionFor(state, row.taskId))) return null;

  const [first] = batch.rows;
  const targetLabel =
    first?.steps.find((step) => step.stepId === batch.outcome.to)?.label ??
    batch.outcome.to;
  const subject = `${String(batch.rows.length)} subtasks of ${group.parent?.title ?? 'this task'}`;

  return (
    <Button
      type="button"
      size="xs"
      variant="outline"
      aria-label={`${batch.label}: ${subject}`}
      className="ms-auto shrink-0 pointer-coarse:min-h-11"
      onClick={() => {
        const held = hold({
          choice: { kind: 'decide', outcome: batch.outcome, targetLabel },
          rows: batch.rows,
          label: batch.label,
          subject,
        });
        if (held) onHeld();
      }}
    >
      <CheckCheckIcon aria-hidden="true" strokeWidth={1.5} />
      {batch.label}
    </Button>
  );
}

/** Offered only to someone who may work in the workspace (spec 189). */
export function BatchAction(props: Parameters<typeof BatchActionControl>[0]) {
  return useCanWork() ? <BatchActionControl {...props} /> : null;
}
